import { forwardRef, ReactNode, useEffect } from 'react';
import { Link, LinkProps } from 'react-router-dom';

// Igual ao `<title>` de `public/index.html`: é também o título padrão da aba.
const SUFIXO_DO_TITULO_DA_ABA = '#JogaJunto BB';

/** Voltar por rota: renderiza `Link`. */
type VoltarParaRota = {
  to: LinkProps['to'];
  /** Completa o nome acessível: "Voltar para {destino}". */
  destino: string;
  onClick?: never;
};

/** Voltar por ação (ex.: fechar um painel na mesma rota): renderiza `<button>`. */
type VoltarPorAcao = {
  onClick: () => void;
  /** Completa o nome acessível: "Voltar para {destino}". */
  destino: string;
  to?: never;
};

export type PageHeaderVoltar = VoltarParaRota | VoltarPorAcao;

interface PageHeaderProps {
  /** Texto do `h1` e do título da aba do navegador. */
  titulo: string;
  voltar?: PageHeaderVoltar;
  /** Linha abaixo do título; a página pode colorir trechos. */
  subtitulo?: ReactNode;
  /** À direita da linha do título (seletor de ciclo, chip…). */
  acoes?: ReactNode;
  /** Logo depois do título, na mesma linha (selo de perfil). */
  selo?: ReactNode;
}

// Mesmo anel de foco do `Button` da nova identidade.
const VOLTAR_CLASSES =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-jj-full bg-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue-dark';

/*
 * Traço em `var(--brand-yellow)` e não `currentColor`: o `rsuite.min.css` pinta
 * `a:hover`/`a:focus` de outra cor, e o chevron herdaria essa cor no link.
 */
function ChevronVoltar() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--brand-yellow)"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function BotaoVoltar({ voltar }: { voltar: PageHeaderVoltar }) {
  const nomeAcessivel = `Voltar para ${voltar.destino}`;

  if (voltar.to !== undefined) {
    return (
      <Link to={voltar.to} aria-label={nomeAcessivel} className={VOLTAR_CLASSES}>
        <ChevronVoltar />
      </Link>
    );
  }

  return (
    <button type="button" onClick={voltar.onClick} aria-label={nomeAcessivel} className={VOLTAR_CLASSES}>
      <ChevronVoltar />
    </button>
  );
}

/**
 * Cabeçalho de página da nova identidade: voltar opcional, título (`h1`), ações à
 * direita e subtítulo opcional abaixo.
 *
 * Define o título da aba como "{titulo} | #JogaJunto BB" e volta ao título padrão
 * do app ao desmontar. O `ref` vai para o `h1` (`tabIndex={-1}`), para a página mover o foco
 * até ele sem pôr o título na ordem de Tab.
 */
const PageHeader = forwardRef<HTMLHeadingElement, PageHeaderProps>(function PageHeader(
  { titulo, voltar, subtitulo, acoes, selo },
  ref,
) {
  /*
   * Ao desmontar, volta ao título padrão e não ao capturado na montagem: com dois
   * cabeçalhos montados, o React limpa na ordem da árvore e o último a limpar
   * devolveria o título que o anterior tinha posto.
   */
  useEffect(() => {
    document.title = `${titulo} | ${SUFIXO_DO_TITULO_DA_ABA}`;
    return () => {
      document.title = SUFIXO_DO_TITULO_DA_ABA;
    };
  }, [titulo]);

  return (
    <div className="mb-[22px] flex flex-col gap-[22px]">
      <div className="flex flex-wrap items-center gap-3.5">
        {voltar && <BotaoVoltar voltar={voltar} />}
        {/* `leading-[normal]`: o `rsuite.min.css` põe `line-height: 62px` em todo `h1`, e a linha do título
            ficava com 62px em vez dos 36px do design. */}
        <h1 ref={ref} tabIndex={-1} className="m-0 font-display text-[28px] font-extrabold leading-[normal] text-brand-blue-dark">
          {titulo}
        </h1>
        {selo}
        {acoes && <div className="ml-auto">{acoes}</div>}
      </div>
      {subtitulo && <p className="m-0 font-display text-[22px] font-bold text-brand-blue-dark">{subtitulo}</p>}
    </div>
  );
});

export default PageHeader;
